<template>
  <div :class="containerClass" @click="onWrapperClick">
    <AppTopBar v-if="loggedIn" @menu-toggle="onMenuToggle" @logout="onLogout" @settings-click="onSettingsClick" @toggle-dark-mode="onToggleDarkMode" />

    
    <Toast position="bottom-right" />
    <ConfirmDialog />
    <div class="layout-main-container">
      <div v-if="loggedIn" class="card card-w-title">
        <Menubar :model="menu">
          <template #end>
            <span class="p-input-icon-left">
              <i class="pi pi-search" />
              <InputText type="text" placeholder="Search" />
            </span>
          </template>
        </Menubar>
      </div>
      <div class="layout-main">
        <ErrorBoundary>
          <router-view :key="$route.fullPath" />
        </ErrorBoundary>
      </div>
      <AppFooter />
    </div>

    <transition name="layout-mask">
      <div class="layout-mask p-component-overlay" v-if="mobileMenuActive"></div>
    </transition>
  </div>
</template>

<script lang="ts" >
import AppTopBar from "./AppTopbar.vue";
//import AppMenu from "./AppMenu.vue";
import AppFooter from "./AppFooter.vue";
import AppConfig from "./AppConfig.vue";
import ErrorBoundary from "./components/ErrorBoundary.vue";

import { mapGetters, mapActions } from "vuex";

export default {
  data () {
    return {
      layoutMode: "overlay",
      layoutColorMode: "dark",
      staticMenuInactive: true,
      overlayMenuActive: false,
      mobileMenuActive: false,
      configVisible: false,
    };
  },
  watch: {
    $route () {
      this.menuActive = false;
      this.$toast.removeAllGroups();
    },
    errorMessage (message, prevMessage) {
      console.log("error message", message, prevMessage);
      if (message && message != "") {
        console.log("Error Toast");
        this.$toast.add({
          severity: "error",
          summary: "Error",
          detail: message,
          life: 6000,
        });
        this.resetToast();
      }
    },
    successMessage (message, prevMessage) {
      console.log("success message", message, prevMessage);
      if (message && message != "") {
        console.log("Success Toast");
        this.$toast.add({
          severity: "success",
          summary: "Success",
          detail: message,
          life: 6000,
        });
        this.resetToast();
      }
    },
  },
  methods: {
    ...mapActions({
      logout: "auth/logout",
      resetToast: "toast/reset"
    }),
    navigateTo(route) {
      if (route) {
        this.$router.push(route);
      }
    },
    // Hands the current session straight to FreightOps Admin — it reuses this token against the
    // same API rather than authenticating again, so there's nothing to log into over there.
    openNovaAdmin () {
      const adminUrl = window.config?.VITE_ADMIN_URL || "https://admin.novafreightops.com/";
      const apiHost = window.config?.VITE_API_HOST || "";
      const url = `${adminUrl}?u=${encodeURIComponent(this.username)}&t=${encodeURIComponent(this.token)}&api=${encodeURIComponent(apiHost)}`;

      window.open(url, "_blank", "noopener,noreferrer");
    },
    onLogout () {
      this.logout().then(() => {
        // "Home" is neither a path nor a route name, so vue-router treated it
        // as a relative path, matched nothing, and aborted the navigation --
        // leaving the user logged out but still sitting on the current page.
        this.$router.push("/login");
      });
    },
    onWrapperClick () {
      if (!this.menuClick) {
        this.overlayMenuActive = false;
        this.mobileMenuActive = false;
      }

      this.menuClick = false;
    },
    onMenuToggle (event) {
      this.menuClick = true;

      if (this.isDesktop()) {
        if (this.layoutMode === "overlay") {
          if (this.mobileMenuActive === true) {
            this.overlayMenuActive = true;
          }

          this.overlayMenuActive = !this.overlayMenuActive;
          this.mobileMenuActive = false;
        } else if (this.layoutMode === "static") {
          this.staticMenuInactive = !this.staticMenuInactive;
        }
      } else {
        this.mobileMenuActive = !this.mobileMenuActive;
      }

      event.preventDefault();
    },
    onSidebarClick () {
      this.menuClick = true;
    },
    onMenuItemClick (event) {
      if (event.item && !event.item.items) {
        this.overlayMenuActive = false;
        this.mobileMenuActive = false;
      }
    },
    onLayoutChange (layoutMode) {
      this.layoutMode = layoutMode;
    },
    onLayoutColorChange (layoutColorMode) {
      this.layoutColorMode = layoutColorMode;
    },
    onSettingsClick (event) {
      event?.preventDefault();
      console.log("onSettingsClick - before toggle", this.configVisible);
      this.configVisible = !this.configVisible;
      console.log("onSettingsClick - after toggle", this.configVisible);
      this.$nextTick(() => {
        console.log("onSettingsClick - nextTick", this.configVisible);
      });
    },
    async onToggleDarkMode (newValue) {
      // Call AppConfig's toggleDarkMode method since it has the working implementation
      // @ts-ignore
      if (this.$refs.appConfig) {
        // @ts-ignore
        await this.$refs.appConfig.toggleDarkMode(newValue);
      }
    },
    addClass (element, className) {
      if (element.classList) element.classList.add(className);
      else element.className += " " + className;
    },
    removeClass (element, className) {
      if (element.classList) element.classList.remove(className);
      else
        element.className = element.className.replace(
          new RegExp("(^|\\b)" + className.split(" ").join("|") + "(\\b|$)", "gi"),
          " "
        );
    },
    isDesktop () {
      return window.innerWidth >= 992;
    },
    isSidebarVisible () {
      if (this.isDesktop()) {
        if (this.layoutMode === "static") return !this.staticMenuInactive;
        else if (this.layoutMode === "overlay") return this.overlayMenuActive;
      }

      return true;
    },
  },
  computed: {
    menu() {
      return [
        {
          label: "Home",
          items: [
            {
              label: "Dashboard",
              icon: "pi pi-fw pi-home",
              command: () => this.navigateTo("/"),
            },
          ],
        },
        {
          label: "Search",
          items: [
            {
              label: "Global Search",
              icon: "pi pi-fw pi-search",
              command: () => this.navigateTo("/search"),
            },
          ],
        },
        {
          label: "Pros",
          icon: "pi pi-fw pi-home",
          command: () => this.navigateTo("/pros/list"),
        },
        {
          label: "Manifests",
          icon: "pi pi-fw pi-home",
          command: () => this.navigateTo("/manifests/list"),
        },
        {
          label: "Inventory",
          items: [
            {
              label: "Inventory",
              icon: "pi pi-fw pi-home",
              command: () => this.navigateTo("/inventory/list"),
            },
            {
              label: "Terminal Inventory",
              icon: "pi pi-fw pi-home",
              command: () => this.navigateTo("/inventory/terminals"),
            },
            {
              label: "Inventory Maintenance",
              icon: "pi pi-fw pi-home",
              command: () => this.navigateTo("/inventory/maintenance"),
            },
          ],
        },
        {
          label: "Tracking Units",
          items: [
            {
              label: "Lookup",
              icon: "pi pi-fw pi-search",
              command: () => this.navigateTo("/trackingunits/lookup"),
            },
          ],
        },
        {
          label: "Orphan Items",
          icon: "pi pi-fw pi-box",
          command: () => this.navigateTo("/orphans"),
        },
        ...(this.canProcessOsd
          ? [
            {
              label: "Exception",
              items: [
                {
                  label: "Work Queue",
                  icon: "pi pi-fw pi-exclamation-triangle",
                  command: () => this.navigateTo("/osd/queue"),
                },
                {
                  label: "Destroyed Items",
                  icon: "pi pi-fw pi-trash",
                  command: () => this.navigateTo("/osd/destroyed"),
                },
                {
                  label: "Counts Report",
                  icon: "pi pi-fw pi-chart-bar",
                  command: () => this.navigateTo("/osd/counts"),
                },
              ],
            },
          ]
          : []),
        {
          label: "Reports",
          items: [
            {
              label: "Terminal Productivity",
              icon: "pi pi-fw pi-search",
              command: () => this.navigateTo("/reports/terminalproductivity"),
            },
          ],
        },
        ...(this.isAdmin
          ? [
            {
              label: "Admin",
              items: [
                {
                  label: "Postal Codes",
                  icon: "pi pi-fw pi-home",
                  command: () => this.navigateTo("/corporate/postalcodes/list"),
                },
                {
                  label: "Terminals",
                  icon: "pi pi-fw pi-home",
                  command: () => this.navigateTo("/corporate/terminals/list"),
                },
                {
                  label: "Doors",
                  icon: "pi pi-fw pi-home",
                  command: () => this.navigateTo("/corporate/doors/list"),
                },
                {
                  label: "Locations",
                  icon: "pi pi-fw pi-home",
                  command: () => this.navigateTo("/corporate/locations/list"),
                },
                {
                  label: "Lanes",
                  icon: "pi pi-fw pi-home",
                  command: () => this.navigateTo("/corporate/lanes/list")
                },
                {
                  label: "New Lane",
                  icon: "pi pi-fw pi-home",
                  command: () => this.navigateTo("/corporate/lanes/")
                },
                {
                  label: "Zones",
                  icon: "pi pi-fw pi-home",
                  command: () => this.navigateTo("/corporate/zones/list")
                },
                {
                  label: "New Zone",
                  icon: "pi pi-fw pi-home",
                  command: () => this.navigateTo("/corporate/zones/")
                },
                {
                  label: "Users",
                  icon: "pi pi-fw pi-home",
                  command: () => this.navigateTo("/corporate/users/list")
                },
                {
                  label: "Integrations",
                  icon: "pi pi-fw pi-send",
                  command: () => this.navigateTo("/corporate/integrations"),
                },
                {
                  label: "FO Admin",
                  icon: "pi pi-fw pi-external-link",
                  command: () => this.openNovaAdmin(),
                },
              ],
            },
          ]
          : []),
      ];
    },
    containerClass () {
      return [
        "layout-wrapper",
        {
          "layout-overlay": this.layoutMode === "overlay",
          "layout-static": this.layoutMode === "static",
          "layout-static-sidebar-inactive":
            this.staticMenuInactive && this.layoutMode === "static",
          "layout-overlay-sidebar-active":
            this.overlayMenuActive && this.layoutMode === "overlay",
          "layout-mobile-sidebar-active": this.mobileMenuActive,
          "p-input-filled": this.$primevue.config.inputStyle === "filled",
          "p-ripple-disabled": this.$primevue.config.ripple === false,
          "layout-theme-light": this.$appState.theme.startsWith("saga"),
        },
      ];
    },
    logo () {
      return this.layoutColorMode === "dark"
        ? "images/logo-white.svg"
        : "images/logo.svg";
    },
    ...mapGetters({
      loggedIn: "auth/loggedIn",
      isAdmin: "auth/isAdmin",
      canProcessOsd: "auth/canProcessOsd",
      token: "auth/token",
      username: "auth/user",
      errorMessage: "toast/getErrorMessage",
      successMessage: "toast/getSuccessMessage",
    }),
  },
  beforeUpdate () {
    if (this.mobileMenuActive) this.addClass(document.body, "body-overflow-hidden");
    else this.removeClass(document.body, "body-overflow-hidden");
  },
  components: {
    AppTopBar: AppTopBar,
    //AppMenu: AppMenu,
    AppFooter: AppFooter,
    AppConfig: AppConfig,
    ErrorBoundary: ErrorBoundary,
  },
};
</script>

<style lang="scss">
@use "./App.scss";
</style>
